/**
 * The menu behind the user row at the bottom of the sidebar.
 *
 * Wraps the row (passed as `children`) in a dropdown trigger. The menu shows
 * who is signed in, the role switcher, links that depend on the role, quick
 * theme and language controls, Settings, and Log out with the existing
 * confirmation dialog.
 */

import {
	Bell,
	FolderOpen,
	LogOut,
	type LucideIcon,
	Settings,
	ShieldCheck,
	UserRound,
	UsersRound,
	Wallet,
} from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LogoutConfirmationModal } from "@/components/modals/LogoutConfirmationModal";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLogout } from "@/features/auth/hooks/use-logout";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { AddRoleDialog } from "@/features/roles/components/add-role-dialog";
import { useUserRoles } from "@/features/roles/hooks/use-user-roles";
import {
	LANGUAGES,
	useLanguageChoice,
} from "@/features/settings/components/language-switcher";
import { THEME_MODE_OPTIONS } from "@/features/settings/components/theme-mode-switcher";
import { useNotificationsStore } from "@/features/settings/store/notifications.store";
import { useThemeMode } from "@/hooks/use-theme-mode";
import { cn } from "@/lib/utils";
import { RoleSwitcher } from "./role-switcher";

type UserType = "homeowner" | "designer" | "contractor" | "realtor";

interface MenuLink {
	labelKey: string;
	to: string;
	Icon: LucideIcon;
}

/**
 * The page where the role manages its own profile. The public pages that
 * other people see are separate (for example /portfolio/:slug).
 */
const PROFILE_PATH: Record<UserType, string> = {
	homeowner: "/profile/edit",
	designer: "/portfolio",
	contractor: "/contractor-profile",
	realtor: "/realtor/portfolio",
};

/** Shortcuts to pages that exist for that role only. */
const ROLE_LINKS: Record<UserType, MenuLink[]> = {
	homeowner: [
		{ labelKey: "userMenu.myProjects", to: "/projects", Icon: FolderOpen },
	],
	designer: [
		{
			labelKey: "userMenu.payoutAccount",
			to: "/settings#payout",
			Icon: Wallet,
		},
	],
	contractor: [
		{
			labelKey: "userMenu.payoutAccount",
			to: "/settings#payout",
			Icon: Wallet,
		},
	],
	realtor: [
		{
			labelKey: "userMenu.referrals",
			to: "/realtor/referrals",
			Icon: UsersRound,
		},
		{
			labelKey: "userMenu.verification",
			to: "/realtor/verification",
			Icon: ShieldCheck,
		},
	],
};

function isUserType(value: unknown): value is UserType {
	return typeof value === "string" && value in PROFILE_PATH;
}

/** A small segment in the theme and language rows. */
const segmentClass =
	"inline-flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-md px-1.5 text-[11px] font-medium text-dashboard-muted outline-none hover:text-dashboard-text focus:bg-dashboard-divider aria-checked:bg-dashboard-divider aria-checked:text-dashboard-accent";

const itemClass =
	"flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-dashboard-text focus:bg-dashboard-divider";

export function UserMenu({
	name,
	onNavigate,
	children,
}: {
	/** The display name, when the sidebar has one. */
	name?: string;
	/** Called after a menu link navigates, e.g. to close the mobile sidebar. */
	onNavigate?: () => void;
	/** The sidebar row that opens the menu. */
	children: React.ReactElement;
}): React.JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuthStore();
	const { logout } = useLogout();
	const { mode, setMode } = useThemeMode();
	const { currentLang, changeLanguage } = useLanguageChoice();
	const unread = useNotificationsStore(
		(s) => s.notifications.filter((n) => !n.isRead).length,
	);
	const [confirmLogout, setConfirmLogout] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [addRoleOpen, setAddRoleOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	// Set by a switch, which moves focus to the new page's heading itself.
	const leftForSwitch = useRef(false);
	const { data: roles } = useUserRoles();

	const userType = isUserType(user?.userType) ? user.userType : null;
	const roleLabel = userType ? t(`userTypes.${userType}`) : t("userTypes.user");

	const go = (to: string): void => {
		navigate(to);
		onNavigate?.();
	};

	const links: MenuLink[] = [
		...(userType
			? [
					{
						labelKey: "userMenu.myProfile",
						to: PROFILE_PATH[userType],
						Icon: UserRound,
					},
				]
			: []),
		...(userType ? ROLE_LINKS[userType] : []),
	];

	return (
		<>
			<DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
				<DropdownMenuTrigger asChild ref={triggerRef}>
					{children}
				</DropdownMenuTrigger>
				<DropdownMenuContent
					onCloseAutoFocus={(event) => {
						if (!leftForSwitch.current) return;
						leftForSwitch.current = false;
						event.preventDefault();
					}}
					side="top"
					align="start"
					sideOffset={8}
					className="w-64 rounded-xl border border-dashboard-border bg-dashboard-paper p-1 text-dashboard-text shadow-[var(--dashboard-shadow-3)]"
				>
					<div className="px-2 pb-1.5 pt-1">
						<p className="truncate text-[13px] font-semibold text-dashboard-text">
							{name || user?.phone || roleLabel}
						</p>
						<p className="truncate text-[11px] text-dashboard-muted">
							{roleLabel}
							{name && user?.phone ? ` · ${user.phone}` : ""}
						</p>
					</div>
					<DropdownMenuSeparator className="bg-dashboard-border" />

					<RoleSwitcher
						onAddRole={() => setAddRoleOpen(true)}
						onSwitched={() => {
							leftForSwitch.current = true;
							setMenuOpen(false);
						}}
					/>

					{links.map(({ labelKey, to, Icon }) => (
						<DropdownMenuItem
							key={to + labelKey}
							className={itemClass}
							onSelect={() => go(to)}
						>
							<Icon className="h-3.5 w-3.5 text-dashboard-muted" />
							{t(labelKey)}
						</DropdownMenuItem>
					))}
					<DropdownMenuItem
						className={itemClass}
						onSelect={() => go("/notifications")}
					>
						<Bell className="h-3.5 w-3.5 text-dashboard-muted" />
						{t("userMenu.notifications")}
						{unread > 0 && (
							<span
								role="status"
								aria-label={t("userMenu.unread")}
								className="ml-auto h-2 w-2 rounded-full bg-dashboard-accent"
							/>
						)}
					</DropdownMenuItem>
					<DropdownMenuItem
						className={itemClass}
						onSelect={() => go("/settings")}
					>
						<Settings className="h-3.5 w-3.5 text-dashboard-muted" />
						{t("userMenu.settings")}
					</DropdownMenuItem>

					<DropdownMenuSeparator className="bg-dashboard-border" />
					{/*
					  Each choice is its own menu item, so the arrow keys reach it;
					  Radix keeps Tab inside the menu. preventDefault keeps the menu
					  open after a choice.
					*/}
					<div className="flex items-center justify-between px-2 py-1">
						<span className="text-[13px] text-dashboard-text">
							{t("settings.appearance")}
						</span>
						<fieldset
							aria-label={t("settings.appearance")}
							className="m-0 flex min-w-0 gap-0.5 border-0 p-0"
						>
							{THEME_MODE_OPTIONS.map(({ mode: option, labelKey, Icon }) => (
								<DropdownMenuItem
									key={option}
									role="menuitemradio"
									aria-checked={mode === option}
									aria-label={t(labelKey)}
									title={t(labelKey)}
									className={segmentClass}
									onSelect={(event) => {
										event.preventDefault();
										setMode(option);
									}}
								>
									<Icon className="h-3.5 w-3.5" />
								</DropdownMenuItem>
							))}
						</fieldset>
					</div>
					<div className="flex items-center justify-between px-2 py-1">
						<span className="text-[13px] text-dashboard-text">
							{t("settings.language")}
						</span>
						<fieldset
							aria-label={t("settings.language")}
							className="m-0 flex min-w-0 gap-0.5 border-0 p-0"
						>
							{LANGUAGES.map(({ code, label }) => (
								<DropdownMenuItem
									key={code}
									role="menuitemradio"
									aria-checked={currentLang === code}
									className={cn(segmentClass, "px-2")}
									onSelect={(event) => {
										event.preventDefault();
										void changeLanguage(code);
									}}
								>
									{label}
								</DropdownMenuItem>
							))}
						</fieldset>
					</div>

					<DropdownMenuSeparator className="bg-dashboard-border" />
					<DropdownMenuItem
						className={`${itemClass} text-dashboard-pill-risk focus:text-dashboard-pill-risk`}
						onSelect={() => setConfirmLogout(true)}
					>
						<LogOut className="h-3.5 w-3.5" />
						{t("userMenu.logout")}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<AddRoleDialog
				open={addRoleOpen}
				onOpenChange={setAddRoleOpen}
				addableRoles={roles?.addableRoles ?? []}
				unfinishedRoles={roles?.unfinishedRoles ?? []}
				returnFocusTo={triggerRef}
			/>

			<LogoutConfirmationModal
				isOpen={confirmLogout}
				onClose={() => setConfirmLogout(false)}
				onConfirm={logout}
			/>
		</>
	);
}
